import React, { FC, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';

import { trackEvent } from '@xingternal/hops-tracking';

import { SEA_CAMPAIGNS } from '@/helpers/marketing';

import { useSearch } from '../hooks/use-search';
import { useSearchAlertsContext } from './search-alerts-context';
import { useSearchQuery } from './search-query-context';
export type ModalType = 'HIDDEN' | 'DEFAULT' | 'REPEATED_SEARCH' | 'DIALOG';

type SearchAlertModalData = {
  isModalShown: boolean;
  modalType: ModalType;
  isValidQuery: boolean;
  stringifiedQuery: string;
  toggleModal: (param?: { propActionOrigin?: string }) => void;
  openPropActionOrigin?: string;
};

/*
Context that handles all the search alerts modals logic in the Search Page

A modal can be opened if:
- The search contains keywords or location (otherwise we show a dialog for manual triggers)
- The alert doesn't exist (check for LI only)
- The user hasn't reached the maximum number of alerts (check for LI only)

A modal will be opened if (ordered by priority):
- SEA campaign (automatic trigger, checking `sc_cmp` query param)
- Repeated search modal (automatic trigger, if the user has performed the search x times, checking local storage)
- Manual trigger (only possible in LO)
*/
export const SearchAlertModalContext = createContext<SearchAlertModalData>({
  isModalShown: false,
  modalType: 'HIDDEN',
  isValidQuery: false,
  stringifiedQuery: '',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  toggleModal: () => {},
});

const ALERT_PROMPT_AFTER_NTH_TIMES = [3, 9, 16];

export const SearchAlertModalProvider: FC = ({ children }) => {
  const isInitialLoad = useRef(true);
  const [canShowRepeatedModal, setCanShowRepeatedModal] = useState(true);
  const [modalType, setModalType] = useState<ModalType>('HIDDEN');
  const [campaignParam] = useQueryParam('sc_cmp', StringParam);
  const [openPropActionOrigin, setOpenPropActionOrigin] = useState<string | undefined>(undefined);
  const { alertQuery, isAlertActive, areAlertsInitialized, isMaximumReached } = useSearchAlertsContext();
  const { queryCount } = useSearchQuery();
  const { loading: isSearchLoading, data: searchData } = useSearch();
  const [lastSearchQueryIdWithAlert, setlastSearchQueryIdWithAlert] = useState('');

  const { keywords = '', location = '', radius = '' } = alertQuery;
  const stringifiedQuery = `${keywords ?? ''}${keywords && location ? ', ' : ''}${location ?? ''}${
    location && radius ? ` (${radius} km)` : ''
  }`;
  const isValidQuery = Boolean(keywords || location);

  const canTriggerAutomaticModals =
    !isAlertActive && areAlertsInitialized && !isMaximumReached && isValidQuery && !isSearchLoading;
  const isSEACampaign = campaignParam && SEA_CAMPAIGNS.includes(campaignParam);
  const currentSearchQueryId = searchData?.jobSearchByQuery?.searchQuery.guid ?? '';

  const trackOpenModal = (propActionOrigin: string): void => {
    trackEvent('PropTrackAction', {
      PropTrackAction: 'jobs_search_alert_create_open',
      PropActionOrigin: propActionOrigin,
    });
    setOpenPropActionOrigin(propActionOrigin);
  };

  // this useEffect handles all the modal automatic triggers
  useEffect(() => {
    if (canTriggerAutomaticModals) {
      // Check for SEA campaign
      if (isInitialLoad.current && isSEACampaign) {
        setModalType('DEFAULT');
        setCanShowRepeatedModal(false);
        trackOpenModal('jobs_pop_up_partner');
      }
      // Check for repeated search
      else if (
        canShowRepeatedModal &&
        ALERT_PROMPT_AFTER_NTH_TIMES.includes(queryCount) &&
        lastSearchQueryIdWithAlert !== currentSearchQueryId
      ) {
        setModalType('REPEATED_SEARCH');
        setCanShowRepeatedModal(false);
        trackOpenModal('jobs_search_recent_searches');
      }

      isInitialLoad.current = false;
    }
  }, [
    canShowRepeatedModal,
    canTriggerAutomaticModals,
    isSEACampaign,
    lastSearchQueryIdWithAlert,
    queryCount,
    currentSearchQueryId,
  ]);

  // every time a new search is made, repeated modal is allowed to be displayed if conditions are met
  useEffect(() => {
    setCanShowRepeatedModal(true);
  }, [alertQuery]);

  // save the last search query id with alert to avoid showing the repeated modal when an user delete an alert
  useEffect(() => {
    if (isAlertActive) {
      setlastSearchQueryIdWithAlert(currentSearchQueryId);
    }
  }, [isAlertActive, currentSearchQueryId]);

  // toggleModal handles all the modal manual triggers
  const toggleModal = useCallback(
    ({ propActionOrigin } = {}): void => {
      const nextModalType = modalType === 'HIDDEN' ? (isValidQuery ? 'DEFAULT' : 'DIALOG') : 'HIDDEN';

      if (nextModalType === 'DEFAULT' && propActionOrigin) {
        trackOpenModal(propActionOrigin);
      }
      setModalType(nextModalType);
    },
    [isValidQuery, modalType]
  );

  const contextValue = {
    isModalShown: modalType !== 'HIDDEN',
    modalType,
    stringifiedQuery,
    toggleModal,
    isValidQuery,
    openPropActionOrigin,
  };

  return <SearchAlertModalContext.Provider value={contextValue}>{children}</SearchAlertModalContext.Provider>;
};

export const useSearchAlertModal = (): SearchAlertModalData => useContext(SearchAlertModalContext);
